import React, { useState, useCallback, useImperativeHandle, useEffect, useRef, forwardRef } from "react";
import PropTypes from "prop-types";
import { Checkbox } from "primereact/checkbox";
import { InputText, Label, Calendar, Dropdown } from "../../../components/Inputs";
import { labelToUrl, toDBDateTime, toUIInputDate } from "../../../components/Utils";
import { PAGE_TYPES } from "./Utils";

const isUrlUnique = (url, siblingUrls = [], currentUrl = null) => {
  return !siblingUrls.filter(u => u !== currentUrl).includes(url);
};

const PagePathForm = forwardRef((props, ref) => {
  const { className, model, parentUrl, type, nrSiblings, nrOld, nrNew, nrChildren, siblingUrls = [], onValidityChange, isEditing } = props;
  const data = model.data || {};
  const defaultIsActive = (!data.color || data.color === "black");
  const maxPosition = isEditing ? (nrSiblings || siblingUrls.length + 1) : nrChildren;
  const defaultPosition = maxPosition;
  const getPosition = (model) => {
    const pos = (model.position !== undefined && model.position !== null) ? model.position : defaultPosition;
    return pos > maxPosition ? maxPosition : pos;
  };

  const [label, setLabelState] = useState(model.label || "");
  const [url, setUrl] = useState(model.url || `${parentUrl}/`);
  const [isVisible, setIsVisible] = useState(model.is_visible === 1);

  // const urlRef = useRef(url);
  // useEffect(() => { urlRef.current = url; }, [url]);

  // // Update URL when parentUrl changes (e.g., when page is moved to different parent)
  // useEffect(() => {
  //   const currentModelUrl = model.url || "";
  //   const newBaseUrl = `${parentUrl}/${labelToUrl(label)}`;
  //   if (!currentModelUrl || currentModelUrl.startsWith(parentUrl) || label) {
  //     setUrl(newBaseUrl);
  //   }
  // }, [parentUrl]);
  
  // // Update URL when parentUrl changes (e.g., when page is moved to different parent).
  // // Skip initial mount so the URL initialized from model.url is not overridden.
  // // Use urlRef.current (updated by the effect above) to preserve the current slug.
  // const parentUrlMounted = useRef(false);
  // useEffect(() => {
  //   if (!parentUrlMounted.current) {
  //     parentUrlMounted.current = true;
  //     return;
  //   }
  //   const currentSlug = urlRef.current.split('/').pop() || '';
  //   setUrl(`${parentUrl}/${currentSlug}`);
  // }, [parentUrl]);
  const [isActive, setIsActive] = useState(defaultIsActive);
  const [position, setPosition] = useState(getPosition(model));
  const [publicationDate, setPublicationDate] = useState(toUIInputDate(data.publicationDate));
  const [endDate, setEndDate] = useState(toUIInputDate(data.endDate));
  const [error, setError] = useState({ label: null, url: null });

  const validate = useCallback(() => {
    const nextError = { ...error };
    const nextModel = {
      label,
      url,
      isVisible,
      isActive,
      position,
      publicationDate,
      endDate,
    };
    // check if all values are set
    let isValid = true;
    Object.keys(nextModel).forEach((key) => {
      if (nextError[key] !== undefined && nextModel[key] === "") {
        nextError[key] = true;
        isValid = false;
      }
    });
    // check URL uniqueness among siblings
    const siblingUrlsExcludingSelf = siblingUrls.filter(u => u !== model.url);
    if (siblingUrlsExcludingSelf.includes(url)) {
      nextError.url = "Diese URL existiert bereits";
      isValid = false;
    }
    // position
    if (!isValid) {
      setError(nextError);
      return null;
    }
    if (model.id) {
      nextModel.id = model.id;
    }
    return nextModel;
  }, [error, label, url, isVisible, isActive, position, publicationDate, endDate, model.id, siblingUrls]);

  // Notify parent of validity changes and update error state for URL collision
  useEffect(() => {
    const hasUrlCollision = !isUrlUnique(url, siblingUrls, model.url);
    setError(prev => ({
      ...prev,
      url: hasUrlCollision ? "Diese URL existiert bereits" : null,
    }));
    if (onValidityChange) {
      onValidityChange(!hasUrlCollision && !!label);
    }
  }, [url, siblingUrls, model.url, label, onValidityChange]);

  const getModel = useCallback(() => {
    const currentData = model.data || {};
    const state = validate();
    if (!state) {
      return null;
    }
    // Use ref to get current URL (avoid stale closure)
    // const currentUrl = urlRef.current;
    const result = {
      id: model.id,
      label: state.label,
      url: state.url, //currentUrl,
      oldUrl: model.url,
      is_visible: state.isVisible ? 1 : 0,
      position: state.position,
      data: {
        ...currentData,
        publicationDate: toDBDateTime(state.publicationDate),
        endDate: toDBDateTime(state.endDate)
      }
    };
    return result;
  }, [model, validate]);

  useImperativeHandle(ref, () => ({
    getModel,
  }), [getModel]);

  const setLabel = useCallback((value) => {
    setLabelState(value);
    const newUrl = `${parentUrl}/${labelToUrl(value)}`;
    setUrl(newUrl);
  }, [parentUrl]);

  console.log("PagePathForm.render()", props, "maxPosition:", maxPosition, "position:", position, "defaultPosition:", defaultPosition, "siblingUrls:", siblingUrls, "nrSiblings:", nrSiblings, "nrChildren:", nrChildren);
  const positionOptions = [];
  for (let pos = 1; pos <= maxPosition; pos++) {
    positionOptions.push({
			label: `als ${pos  == 1 ? "Erster" : (pos === maxPosition ? "Letzter" : (pos + "."))}`,
      value: pos
    });
  }

  return (
    <div className={className}>
      <div className={`pad-top- form-group-item${error.label ? " error-group" : ""}`}>
        <Label htmlFor="label">{`Linktitel`}*</Label>
        <InputText
          id="label"
          className="w3-block w3-border"
          value={label}
          onChange={(event) => setLabel(event.target.value)}
          // maxLength="30"
        />
      </div>
      <div className={`pad-top form-group-item${error.url ? " error-group" : ""}`}>
        <Label htmlFor="url">{`Pfad`}*</Label>
        <InputText
          id="url"
          className="w3-block w3-border"
          value={url}
          disabled={true/* !isEditing */}
          onChange={(event) => setUrl(event.target.value)}
        />
        {error.url && <div className="error-message">{error.url}</div>}
      </div>
      <div className="w3-row neg-margin-lr">
        <div className="w3-col s6 pad-lr">
          <div className={`pad-big-top pad-btm`}>
            <div className={`p-col-12`}>
              <Checkbox
                inputId={`isVisible`}
                value={"isVisible"}
                onChange={(event) => setIsVisible(event.checked)}
                checked={isVisible}
              />
              <label htmlFor={`isVisible`} className="p-checkbox-label bold">{"ist sichtbar"}</label>
            </div>
          </div>
        </div>
        {/* <div className="w3-col s6 pad-lr">
          {isActiveMarkup}
        </div> */}
      </div>
      <div className="w3-row neg-margin-lr">
        <div className="w3-col s6 pad-lr">
          <div className="w3-row neg-margin-lr">
            <div className="w3-col s6 pad-lr">
              <div className="formgroup-item pad-top">
                <Label htmlFor="publicationDate">{"Von"}</Label>
                <Calendar
                  id="publicationDate"
                  className="w3-block w3-border w3-round"
                  value={publicationDate}
                  dateFormat="dd.mm.yy"
                  onChange={(event) => setPublicationDate(event.value)}
                  showButtonBar
                />
              </div>
            </div>
            <div className="w3-col s6 pad-lr">
              <div className="formgroup-item pad-top">
                <Label htmlFor="endDate">{"Bis"}</Label>
                <Calendar
                  id="endDate"
                  className="w3-block w3-border w3-round"
                  value={endDate}
                  dateFormat="dd.mm.yy"
                  onChange={(event) => setEndDate(event.value)}
                  showButtonBar
                />
              </div>
            </div>
          </div>
        </div>
        <div className="w3-col s6 pad-lr">
          <div className="pad-top form-group-item">
            <Label htmlFor="position">{`Position`}</Label>
            <Dropdown
              id="position"
              // className="w3-block w3-border"
              value={position}
              placeholder="als Letzer"
              options={positionOptions}
              onChange={(event) => setPosition(event.value)}
              // showClear={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
});

PagePathForm.propTypes = {
  className: PropTypes.string,
  model: PropTypes.shape({}),
  parentUrl: PropTypes.string,
  isEditing: PropTypes.bool,
  type: PropTypes.string,
  nrOld: PropTypes.number,
  nrNew: PropTypes.number,
  nrChildren: PropTypes.number,
  siblingUrls: PropTypes.arrayOf(PropTypes.string),
  onValidityChange: PropTypes.func,
};

PagePathForm.defaultProps = {
  className: "",
  model: {
    label: "",
    url: "",
    is_visible: 0,
  },
  parentUrl: "",
  type: PAGE_TYPES.DEFAULT,
  nrOld: 0,
  nrNew: 0,
  nrChildren: 0,
  siblingUrls: [],
};

export default PagePathForm;
